import { useState, useCallback } from 'react';
import api from '../api/api'; // Import your API utility or instance

export const usePromoCode = (charges, CURRENCY_CODE) => {
  const [discount, setDiscount] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState('');

  // Calculate discount based on promo data and order total
  const calculateDiscount = useCallback(
    (discountDetails) => {
      const total = Object.values(charges).reduce((acc, value) => acc + parseFloat(value), 0);
      const { discount_type, discount_value, min_order_value, start_date, end_date, usage_limit } = discountDetails;
      const currentDate = new Date();
      const promoStart = new Date(start_date);
      const promoEnd = new Date(end_date);

      if (currentDate >= promoStart && currentDate <= promoEnd && total >= parseFloat(min_order_value)) {
        let amount = 0;

        // Calculate amount based on discount type
        if (discount_type === 'percentage') {
          amount = (total * parseFloat(discount_value)) / 100;
        } else if (discount_type === 'fixed') {
          amount = parseFloat(discount_value);
        }

        // Ensure discount does not exceed total or usage limit
        amount = Math.min(amount, total);
        if (usage_limit && amount > usage_limit) amount = usage_limit;

        setDiscountAmount(amount);
        setPromoSuccess(`Promo applied! 🎉 You saved ${amount.toFixed(2)} ${CURRENCY_CODE}`);
      } else {
        setDiscountAmount(0);
        setPromoError('Promo code is invalid or does not meet the minimum order value.');
      }
    },
    [charges, CURRENCY_CODE]
  );

  const calculateSpecicalDiscount = useCallback(
    (discountDetails, chargedAmount) => {
      const total = chargedAmount;
      const { discount_type, discount_value, min_order_value, start_date, end_date, usage_limit } = discountDetails;
      const currentDate = new Date();
      const promoStart = new Date(start_date);
      const promoEnd = new Date(end_date);

      if (currentDate >= promoStart && currentDate <= promoEnd && total >= parseFloat(min_order_value)) {
        let amount = 0;

        // Calculate amount based on discount type
        if (discount_type === 'percentage') {
          amount = (total * parseFloat(discount_value)) / 100;
        } else if (discount_type === 'fixed') {
          amount = parseFloat(discount_value);
        }

        // Ensure discount does not exceed total or usage limit
        amount = Math.min(amount, total);
        if (usage_limit && amount > usage_limit) amount = usage_limit;

        setDiscountAmount(amount);
        setPromoSuccess(`Promo applied! 🎉 You saved ${amount.toFixed(2)} ${CURRENCY_CODE}`);
      } else {
        setDiscountAmount(0);
        setPromoError('Promo code is invalid or does not meet the minimum order value.');
      }
    },
    [charges, CURRENCY_CODE]
  );

  // Validate and apply promo code
  const applyPromoCode = useCallback(
    async (promoCode) => {
      if (!promoCode.trim()) {
        setPromoError('Please enter a promo code.');
        return;
      }

      setPromoError('');
      setPromoSuccess('');
      setDiscountAmount(0);

      try {
        const response = await api.get(`/promo-codes/${promoCode.trim()}`);
        const discountDetails = response.data;

        if (!response.data || response.status === false) {
          setPromoError('Invalid promo code. Please try again.');
        } else {
          setDiscount(discountDetails);
          calculateDiscount(discountDetails);
        }
      } catch (error) {
        // Handle different error scenarios with user-friendly messages
        if (error.response) {
          const { status, data } = error.response;

          switch (status) {
            case 404:
              setPromoError('❌ Promo code not found. Please check the code and try again.');
              break;
            case 422:
              setPromoError('❌ Invalid promo code format. Please check and try again.');
              break;
            case 400:
              if (data && data.message) {
                setPromoError(`❌ ${data.message}`);
              } else {
                setPromoError('❌ This promo code cannot be applied. Please try a different code.');
              }
              break;
            case 403:
              setPromoError('❌ This promo code has expired or is no longer valid.');
              break;
            case 429:
              setPromoError('❌ Too many attempts. Please wait a moment and try again.');
              break;
            case 500:
              setPromoError('❌ Server error. Please try again in a few moments.');
              break;
            default:
              setPromoError('❌ Unable to apply promo code. Please try again.');
          }
        } else if (error.request) {
          // Network error
          setPromoError('❌ Network error. Please check your connection and try again.');
        } else {
          // Other errors
          setPromoError('❌ Something went wrong. Please try again later.');
        }
      }
    },
    [calculateDiscount, calculateSpecicalDiscount]
  );

  return {
    discount,
    discountAmount,
    promoError,
    promoSuccess,
    applyPromoCode,
    calculateDiscount,
    calculateSpecicalDiscount
  };
};
